import {
  doc,
  getDoc,
  runTransaction,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore"
import { db } from "../../../lib/firebase"
import {
  generateRoomCode,
  shuffle,
  SCATTERGORIES_LETTERS,
  PLAYER_COLORS,
  computeScores
} from "../../../utils/scattergoriesLogic"
import { getLists } from "../../../data/scattergoriesLists"

const ID_KEY = "scattergories_player_id"
const NAME_KEY = "scattergories_player_name"

export const roomRef = (code) => doc(db, "scattergories_rooms", code.toUpperCase())

export function getIdentity() {
  let playerId = localStorage.getItem(ID_KEY)
  if (!playerId) {
    playerId = Math.random().toString(36).slice(2, 10)
    localStorage.setItem(ID_KEY, playerId)
  }
  return { playerId, name: localStorage.getItem(NAME_KEY) || "" }
}

export function saveIdentity(name) {
  localStorage.setItem(NAME_KEY, name)
}

const twelveHours = () => new Date(Date.now() + 12 * 60 * 60 * 1000)

function playerEntry(name, color) {
  return { name, color, score: 0, joinedAt: Date.now() }
}

export async function createRoom(hostName) {
  const { playerId } = getIdentity()
  saveIdentity(hostName)
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateRoomCode()
    const created = await runTransaction(db, async (tx) => {
      const snap = await tx.get(roomRef(code))
      if (snap.exists()) return false
      tx.set(roomRef(code), {
        phase: "lobby",
        round: 0,
        totalRounds: 3,
        duration: 120, // seconds
        packIds: ["general"],
        hostId: playerId,
        createdAt: serverTimestamp(),
        expireAt: twelveHours(),
        players: { [playerId]: playerEntry(hostName, PLAYER_COLORS[0]) },
        usedLetters: [],
        usedLists: [],
        answers: {}, // roundIdx -> playerId -> [12 answers]
        vetoes: {}, // roundIdx -> catIdx -> playerId -> boolean
        lastResult: null,
        nextRoom: null,
        history: [],
      })
      return true
    })
    if (created) return code
  }
  throw new Error("could-not-create")
}

export async function joinRoom(code, name) {
  const { playerId } = getIdentity()
  saveIdentity(name)
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists()) throw new Error("not-found")
    const players = snap.data().players || {}
    if (players[playerId]) {
      tx.update(roomRef(code), { [`players.${playerId}.name`]: name })
      return
    }
    const color = PLAYER_COLORS[Object.keys(players).length % PLAYER_COLORS.length]
    tx.update(roomRef(code), {
      [`players.${playerId}`]: playerEntry(name, color),
    })
  })
}

export async function updateRoomSettings(code, settings) {
  await updateDoc(roomRef(code), settings)
}

export async function startGame(code, { totalRounds, duration, packIds }) {
  const lists = getLists(packIds)
  const chosenList = shuffle(lists)[0]
  const chosenLetter = shuffle(SCATTERGORIES_LETTERS)[0]

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "lobby") return
    tx.update(roomRef(code), {
      phase: "writing",
      round: 1,
      totalRounds,
      duration,
      packIds,
      letter: chosenLetter,
      listId: chosenList.id,
      usedLetters: [chosenLetter],
      usedLists: [chosenList.id],
      writingEndAt: Date.now() + duration * 1000,
      answers: {},
      vetoes: {},
      lastResult: null,
      history: [],
    })
  })
}

export async function submitAnswers(code, round, playerId, playerAnswers) {
  await updateDoc(roomRef(code), {
    [`answers.${round}.${playerId}`]: playerAnswers,
  })
}

export async function endWriting(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "writing") return
    tx.update(roomRef(code), { phase: "scoring", activeScoringCat: 0 })
  })
}

export async function setScoringCategory(code, catIdx) {
  await updateDoc(roomRef(code), { activeScoringCat: catIdx })
}

// Deliberately a plain updateDoc, not a transaction. Transaction writes skip the
// local cache, so onSnapshot only fires after the server commits — players saw a
// visible lag on every veto tap and clicked again thinking it missed. This write
// targets one leaf field and needs no read, so updateDoc is both correct and
// latency-compensated: the veto renders on the next frame.
export async function toggleVeto(code, round, catIdx, targetPid, isVetoed) {
  await updateDoc(roomRef(code), {
    [`vetoes.${round}.${catIdx}.${targetPid}`]: isVetoed,
  })
}

export async function finalizeScoring(code, round) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "scoring") return
    const room = snap.data()
    
    const roundAnswers = room.answers?.[round] || {}
    const roundVetoes = room.vetoes?.[round] || {}
    const letter = room.letter || ""
    
    // Tabulate score additions using core scoring engine
    const result = computeScores(roundAnswers, roundVetoes, letter)
    
    const players = { ...room.players }
    Object.entries(result.scores).forEach(([pid, roundScore]) => {
      if (players[pid]) {
        players[pid] = {
          ...players[pid],
          score: players[pid].score + roundScore,
        }
      }
    })

    const roundResult = {
      round: room.round,
      letter,
      listId: room.listId,
      scoresGained: result.scores,
      breakdown: result.breakdown,
    }

    const history = [...(room.history || []), roundResult]
    tx.update(roomRef(code), {
      phase: "reveal",
      players,
      lastResult: roundResult,
      history,
    })
  })
}

export async function nextRound(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase !== "reveal") return
    const room = snap.data()
    if (room.round >= room.totalRounds) {
      tx.update(roomRef(code), { phase: "gameover" })
      return
    }

    const lists = getLists(room.packIds)
    const unusedLists = lists.filter((l) => !(room.usedLists || []).includes(l.id))
    const chosenList = unusedLists.length ? shuffle(unusedLists)[0] : shuffle(lists)[0]

    const unusedLetters = SCATTERGORIES_LETTERS.filter((l) => !(room.usedLetters || []).includes(l))
    const chosenLetter = unusedLetters.length
      ? shuffle(unusedLetters)[0]
      : shuffle(SCATTERGORIES_LETTERS)[0]

    tx.update(roomRef(code), {
      phase: "writing",
      round: room.round + 1,
      letter: chosenLetter,
      listId: chosenList.id,
      usedLetters: [...(room.usedLetters || []), chosenLetter],
      usedLists: [...(room.usedLists || []), chosenList.id],
      writingEndAt: Date.now() + (room.duration || 120) * 1000,
    })
  })
}

export async function endGame(code) {
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(roomRef(code))
    if (!snap.exists() || snap.data().phase === "gameover") return
    tx.update(roomRef(code), { phase: "gameover" })
  })
}

export async function deleteRoom(code) {
  await deleteDoc(roomRef(code))
}


export async function playAgain(code, hostName) {
  const { playerId: hostId } = getIdentity()
  const oldSnap = await getDoc(roomRef(code))
  const oldPlayers = oldSnap.exists() ? oldSnap.data().players || {} : {}
  
  // Create a new room with host name
  const newCode = await createRoom(hostName)
  
  // Carry over the players (with reset scores)
  const carried = {}
  Object.entries(oldPlayers).forEach(([pid, player]) => {
    if (pid === hostId) return
    carried[`players.${pid}`] = {
      name: player.name,
      color: player.color,
      joinedAt: player.joinedAt,
      score: 0,
    }
  })
  if (Object.keys(carried).length > 0) {
    await updateDoc(roomRef(newCode), carried)
  }
  await updateDoc(roomRef(code), { nextRoom: newCode })
  return newCode
}

export function subscribeRoom(code, onData, onError) {
  return onSnapshot(
    roomRef(code),
    (snap) => onData(snap.exists() ? snap.data() : null),
    onError
  )
}
